<script setup>
import { Link, router } from '@inertiajs/vue3'
import FullCalendar from '@fullcalendar/vue3'
import timeGridPlugin from '@fullcalendar/timegrid'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import esLocale from '@fullcalendar/core/locales/es'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useDate } from '@/Composables/useDate'
import { useConfirm } from '@/Composables/useConfirm'

const props = defineProps({
  group: {
    type: Object,
    required: true
  },
  participant: {
    type: Object,
    required: true
  },
  availabilities: {
    type: Array,
    required: true
  }
})

const calendarRef = ref(null)
const isMobile = ref(window.innerWidth <= 768)
const validStartDate = useDate().format('YYYY-MM-DD')
const validEndDate = useDate(props.group.phase.date_end).format('YYYY-MM-DD')
const validEndExclusive = useDate(props.group.phase.date_end).addDay().format('YYYY-MM-DD')
const selectedDate = ref(validStartDate)

const events = ref(
  props.availabilities.map((availability) => ({
    id: availability.id,
    title: '',
    start: useDate(availability.date_start).format('YYYY-MM-DD HH:mm:ss'),
    end: useDate(availability.date_end).format('YYYY-MM-DD HH:mm:ss')
  }))
)

function buildEventsFromAvailabilities(availabilities) {
  return availabilities.map((availability) => ({
    id: availability.id,
    title: '',
    start: useDate(availability.date_start).format('YYYY-MM-DD HH:mm:ss'),
    end: useDate(availability.date_end).format('YYYY-MM-DD HH:mm:ss')
  }))
}

function refreshAvailabilities() {
  router.reload({
    only: ['availabilities'],
    preserveState: true,
    preserveScroll: true,
    onSuccess: (page) => {
      events.value = buildEventsFromAvailabilities(page.props.availabilities ?? [])
    }
  })
}

const initialView = computed(() => {
  return isMobile.value ? 'timeGridDay' : 'timeGridWeek'
})

function updateDeviceType() {
  isMobile.value = window.innerWidth <= 768
}

function getCalendarApi() {
  return calendarRef.value?.getApi() ?? null
}

function toDateOnly(value) {
  return useDate(value).format('YYYY-MM-DD')
}

function toDateTime(value) {
  return useDate(value).format('YYYY-MM-DD HH:mm:ss')
}

function toChipLabel(dateString) {
  const today = useDate().format('YYYY-MM-DD')
  const tomorrow = useDate().addDays(1).format('YYYY-MM-DD')

  if (dateString === today) {
    return 'Hoy'
  }

  if (dateString === tomorrow) {
    return 'Mañana'
  }

  return useDate(dateString).format('ddd D')
}

function getShiftDays() {
  const calendarApi = getCalendarApi()

  if (!calendarApi) {
    return 1
  }

  return calendarApi.view.type === 'timeGridWeek' ? 7 : 1
}

function goToToday() {
  const calendarApi = getCalendarApi()

  if (!calendarApi) {
    return
  }

  calendarApi.today()
}

function goToPrevious() {
  const calendarApi = getCalendarApi()

  if (!calendarApi) {
    return
  }

  calendarApi.incrementDate({ days: -getShiftDays() })
}

function goToNext() {
  const calendarApi = getCalendarApi()

  if (!calendarApi) {
    return
  }

  calendarApi.incrementDate({ days: getShiftDays() })
}

function goToDate(dateString) {
  const calendarApi = getCalendarApi()

  if (!calendarApi) {
    return
  }

  calendarApi.gotoDate(dateString)
}

function onPickExactDate(event) {
  const pickedDate = event.target.value

  if (!pickedDate) {
    return
  }

  goToDate(pickedDate)
}

const dateChips = computed(() => {
  const chips = []
  const centerDate = new Date(`${selectedDate.value}T00:00:00`)
  const minDate = new Date(`${validStartDate}T00:00:00`)
  const maxDate = new Date(`${validEndDate}T00:00:00`)

  for (let offset = -3; offset <= 3; offset += 1) {
    const date = new Date(centerDate)
    date.setDate(centerDate.getDate() + offset)

    if (date < minDate || date > maxDate) {
      continue
    }

    const isoDate = date.toISOString().slice(0, 10)

    chips.push({
      date: isoDate,
      label: toChipLabel(isoDate)
    })
  }

  return chips
})

function hasOverlap(start, end) {
  const startTs = new Date(start).getTime()
  const endTs = new Date(end).getTime()

  return events.value.some((event) => {
    const eventStart = new Date(event.start).getTime()
    const eventEnd = new Date(event.end).getTime()

    return startTs < eventEnd && endTs > eventStart
  })
}

function isInvalidSlot(startString, endString, startDateRef) {
  const dayEnd = `${startDateRef.format('YYYY-MM-DD')} 23:59:59`
  const latestStart = `${startDateRef.format('YYYY-MM-DD')} 22:30:00`

  if (startString > latestStart) {
    return true
  }

  if (endString > dayEnd) {
    return true
  }

  return false
}

function addAvailability(selectInfo) {
  const baseDate = selectInfo.date ?? selectInfo.start

  if (!baseDate) {
    return
  }

  const dateStart = useDate(baseDate)
  const dateEnd = useDate(baseDate).add(1.5, 'hour')
  const start = dateStart.format('YYYY-MM-DD HH:mm:ss')
  const end = useDate(dateEnd).format('YYYY-MM-DD HH:mm:ss')

  if (isInvalidSlot(start, end, dateStart)) {
    return
  }

  if (hasOverlap(start, end)) {
    return
  }

  const calendarApi = selectInfo.view?.calendar ?? selectInfo.view?.calendarApi ?? getCalendarApi()

  if (!calendarApi) {
    return
  }
  const tempId = `tmp-${Date.now()}`

  const localEvent = {
    id: tempId,
    title: '',
    start,
    end
  }

  events.value = [...events.value, localEvent]

  calendarApi.addEvent(localEvent)

  router.put(
    `/phases/groups/${props.group.id}/participant/${props.participant.id}/availability/store`,
    {
      date_start: start,
      date_end: end
    },
    {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        refreshAvailabilities()
      },
      onError: () => {
        events.value = events.value.filter((event) => event.id !== tempId)
        const created = calendarApi.getEventById(tempId)

        if (created) {
          created.remove()
        }
      }
    }
  )
}

async function removeAvailabilityFromEvent(calendarEvent) {
  const shouldDelete = await useConfirm('¿Seguro que quieres eliminar esta disponibilidad?')

  if (!shouldDelete) {
    return
  }

  const eventToDelete = {
    id: calendarEvent.id,
    start: toDateTime(calendarEvent.start),
    end: toDateTime(calendarEvent.end)
  }

  calendarEvent.remove()
  events.value = events.value.filter(
    (event) => !(event.start === eventToDelete.start && event.end === eventToDelete.end)
  )

  router.put(
    `/phases/groups/${props.group.id}/participant/${props.participant.id}/availability/delete`,
    {
      date_start: eventToDelete.start,
      date_end: eventToDelete.end
    },
    {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        refreshAvailabilities()
      },
      onError: () => {
        events.value = [...events.value, eventToDelete]
        const calendarApi = getCalendarApi()

        if (calendarApi) {
          calendarApi.addEvent(eventToDelete)
        }
      }
    }
  )
}

function onDatesSet(info) {
  selectedDate.value = toDateOnly(info.start)
}

function linkWithQuery(path) {
  const currentSearchParams = new URLSearchParams(window.location.search)
  const queryString = currentSearchParams.toString()

  return queryString ? `${path}?${queryString}` : path
}

const calendarOptions = computed(() => ({
  validRange: {
    start: validStartDate,
    end: validEndExclusive
  },
  longPressDelay: 1,
  eventLongPressDelay: 1,
  selectLongPressDelay: 1,
  selectable: true,
  editable: false,
  eventResizableFromStart: false,
  height: 'auto',
  locale: esLocale,
  plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
  initialView: initialView.value,
  allDaySlot: false,
  headerToolbar: false,
  stickyHeaderDates: true,
  slotDuration: '00:30:00',
  snapDuration: '00:30:00',
  slotMinTime: '08:00:00',
  slotMaxTime: '24:00:00',
  eventOverlap: false,
  slotEventOverlap: false,
  selectOverlap: false,
  dayHeaderFormat: { weekday: 'short', day: 'numeric', month: 'short' },
  views: {
    timeGridDay: {
      dayHeaderFormat: { weekday: 'long', day: 'numeric', month: 'long', omitCommas: true }
    }
  },
  slotLabelFormat: {
    hour: '2-digit',
    minute: '2-digit',
    omitZeroMinute: false,
    hour12: false
  },
  eventContent: (arg) => {
    const wrapper = document.createElement('div')
    wrapper.className = 'event-chip'
    wrapper.innerHTML = `
      <span>${useDate(arg.event.start).format('HH:mm')} - ${useDate(arg.event.end).format('HH:mm')}</span>
      <button type="button" class="event-delete-btn">Borrar</button>
    `

    const deleteButton = wrapper.querySelector('.event-delete-btn')

    if (deleteButton) {
      deleteButton.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()
        removeAvailabilityFromEvent(arg.event)
      })
    }

    return { domNodes: [wrapper] }
  },
  dateClick: addAvailability,
  select: addAvailability,
  datesSet: onDatesSet,
  events: events.value
}))

onMounted(() => {
  window.addEventListener('resize', updateDeviceType)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateDeviceType)
})
</script>

<template>
  <div class="availability-page">
    <div class="mx-auto max-w-5xl px-3 sm:px-4">
      <div class="mb-4 sm:mb-6">
        <p class="text-xs uppercase tracking-wide text-gray-500">Disponibilidad</p>
        <h1 class="mt-1 text-base font-semibold text-gray-900 sm:text-xl">
          {{ group.phase.ranking.name }} / {{ group.phase.name }} / {{ group.name }} /
          <Link
            class="text-indigo-600 hover:text-indigo-500"
            :href="linkWithQuery(`/phases/groups/${group.id}/participant/${participant.id}/show`)"
          >
            {{ participant.name }}
          </Link>
        </h1>
      </div>

      <div class="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
        <div
          class="mb-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center lg:grid lg:grid-cols-[auto_auto_auto_auto_1fr_auto] lg:gap-3"
        >
          <button class="nav-btn" type="button" @click="goToPrevious">Anterior</button>
          <button class="nav-btn" type="button" @click="goToToday">Hoy</button>
          <button class="nav-btn" type="button" @click="goToNext">Siguiente</button>
          <input
            class="rounded-lg border border-gray-300 px-2 py-2 text-xs text-gray-700 sm:ml-1 lg:ml-0 lg:w-[168px]"
            type="date"
            :value="selectedDate"
            :min="validStartDate"
            :max="validEndDate"
            @change="onPickExactDate"
          />
          <Link
            class="col-span-2 inline-flex items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 sm:col-span-1 sm:ml-auto sm:w-auto lg:col-span-1 lg:ml-0 lg:justify-self-end lg:px-2.5"
            :href="linkWithQuery(`/phases/groups/${group.id}/participant/${participant.id}/show`)"
          >
            Ver grupo
          </Link>
        </div>

        <div class="mb-3 grid grid-cols-3 gap-2 sm:flex sm:flex-wrap lg:gap-3">
          <button
            v-for="chip in dateChips"
            :key="chip.date"
            type="button"
            class="day-chip"
            :class="{ 'day-chip-active': chip.date === selectedDate }"
            @click="goToDate(chip.date)"
          >
            {{ chip.label }}
          </button>
        </div>

        <p class="mb-3 text-xs text-gray-600 sm:text-sm">
          Toca una franja para crear una disponibilidad de 1h30. Toca una disponibilidad para eliminarla.
        </p>

        <div class="calendar-shell">
          <FullCalendar ref="calendarRef" class="h-full" :options="calendarOptions" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.availability-page {
  padding-bottom: max(12px, env(safe-area-inset-bottom));
}

.calendar-shell {
  min-height: calc(100vh - 280px);
}

.nav-btn {
  border-radius: 0.75rem;
  border: 1px solid #d1d5db;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #111827;
  background: #ffffff;
}

.day-chip {
  flex: 0 0 auto;
  border-radius: 9999px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  padding: 0.4rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
}

.day-chip-active {
  border-color: #4f46e5;
  background: #eef2ff;
  color: #312e81;
}

::v-deep(.fc) {
  --fc-border-color: #e5e7eb;
  --fc-page-bg-color: #ffffff;
}

::v-deep(.fc-toolbar-title) {
  font-size: 0.95rem !important;
  text-transform: capitalize;
}

::v-deep(.fc-timegrid-slot-label-cushion) {
  font-size: 0.72rem;
}

::v-deep(.fc-timegrid-event) {
  border: none;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  border-radius: 0.65rem;
}

::v-deep(.event-chip) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  padding: 0.2rem 0.35rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: #ffffff;
}

::v-deep(.event-delete-btn) {
  border: none;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 0.1rem 0.45rem;
  cursor: pointer;
}

@media (max-width: 768px) {
  .calendar-shell {
    min-height: calc(100vh - 320px);
  }

  ::v-deep(.fc-timegrid-axis-cushion),
  ::v-deep(.fc-timegrid-slot-label-cushion) {
    font-size: 0.67rem;
  }

  ::v-deep(.fc-col-header-cell-cushion) {
    font-size: 0.72rem;
    padding: 0.3rem 0.1rem;
    text-transform: capitalize;
  }

  ::v-deep(.fc-timegrid-slot) {
    height: 2.8rem;
  }
}

@media (min-width: 1024px) {
  .nav-btn {
    padding: 0.4rem 0.6rem;
    font-size: 0.7rem;
  }

  .calendar-shell {
    min-height: calc(100vh - 250px);
  }

  ::v-deep(.fc-timegrid-slot) {
    height: 2.35rem;
  }

  ::v-deep(.fc-timegrid-axis-cushion),
  ::v-deep(.fc-timegrid-slot-label-cushion) {
    font-size: 0.74rem;
  }
}
</style>
